import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface SimpleFadeInProps {
  children: React.ReactNode;
  delay?: number;
  threshold?: number;
}

const FadeWrapper = styled.div<{ $isVisible: boolean; $delay: number }>`
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0.99)};
  transform: ${({ $isVisible }) => ($isVisible ? 'translateY(0)' : 'translateY(10px)')};
  transition: opacity 0.6s ease ${({ $delay }) => $delay}ms,
              transform 0.6s ease ${({ $delay }) => $delay}ms;
  
  /* Fallback for broken animations */
  @media (prefers-reduced-motion: reduce) {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
`;

const SimpleFadeIn: React.FC<SimpleFadeInProps> = ({ 
  children, 
  delay = 0,
  threshold = 0.1 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once visible, stop observing
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return (
    <FadeWrapper ref={ref} $isVisible={isVisible} $delay={delay}>
      {children}
    </FadeWrapper>
  );
};

export default SimpleFadeIn;